/**
 * 69.5f (the owner: "bots 'scouting' way deep in friendly territory, and idle bots just hanging around in friendly territory
 * when there's a battle going on one sector over in foothold"): where a side's front stands under Foothold's Operations, and
 * what lies behind it. Pure. The front is the push's active sector, or the tug's live sectors (core/capturePoints.ts); a
 * side's own territory is the sectors on its side of it (sectors count from team 0's side). Read by the commander's pull
 * (ai/frontPull.ts) and the table's left-behind flag (core/commandTable.ts). No front (no sectors, no push): nothing is behind.
 */
import type { GameState, SquadOrder, Vec3 } from '../types';
import { sectorAt, activeSites, liveSectors } from './capturePoints';
import { orderPlace } from './orderForms';

type FrontState = Pick<GameState, 'modeState'>;

/** The front's sectors, lowest and highest (sector indices from team 0's side), or null with no front. */
export function frontSpan(state: FrontState): { lo: number; hi: number } | null {
  const ps = state.modeState.points;
  if (!ps?.front || !ps.push) return null;
  const front = ps.push.shape === 'tug' ? liveSectors(ps) : [ps.push.active];
  if (!front.length) return null;
  return { lo: Math.min(...front), hi: Math.max(...front) };
}

/** How many sectors `dir` lies behind `team`'s edge of the front (0: at or past it), or 0 with no front. */
export function sectorsBehind(state: FrontState, team: number, dir: Vec3): number {
  const span = frontSpan(state), ps = state.modeState.points;
  if (!span || !ps?.front) return 0;
  const k = sectorAt(ps.front, dir);
  return Math.max(0, team === 0 ? span.lo - k : k - span.hi);
}

/** Is an objective in play being captured (a fight at the front)? */
export function frontFought(state: FrontState): boolean {
  return activeSites(state).some(s => s.capturer !== null);
}

/**
 * 69.5f, the owner's ruling (asked: flag / release after a delay / flag then release; chose flag): a squad whose order in
 * force is a human's, at a place behind its side's front while an objective in play is being fought over, is left behind —
 * the table marks it so the human can re-order it; nothing re-tasks it (68.23: the human's order stands).
 */
export function leftBehind(state: Pick<GameState, 'modeState' | 'config'>, team: number, order: SquadOrder | null, by: string | null): boolean {
  if (state.config.frontPull === false || by !== 'human' || !order) return false;
  if (order.kind !== 'hold' && order.kind !== 'take' && order.kind !== 'move' && order.kind !== 'bound' && order.kind !== 'flank') return false;
  const place = orderPlace(state, order);
  return !!place && sectorsBehind(state, team, place.surfaceDir) > 0 && frontFought(state);
}
